import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';

export type InvoiceSide = 'Purchase' | 'Sale';

export interface AllocatedDownpayment {
  allocated_downpayment: string;
  bl_shipment_count: number;
  total_shipments: number;
  // Present only when the advance-tracking read flag is on and a direction was
  // requested. Their presence — not a client-side flag read — is what switches
  // the pre-fill from the shipment-ratio guess to the side's open advance.
  applied?: string;
  advance_open?: string;
  advance_source?: 'application' | 'legacy' | 'mixed' | 'none';
}

// The order-side open advance, or null when the tracking-read fields are
// absent (flag off) — field presence is the gate the pre-fill keys on.
export function advanceOpenOf(data: AllocatedDownpayment | undefined): number | null {
  return data?.advance_open === undefined ? null : Number(data.advance_open);
}

// Hierarchical key: the direction is the last, optional segment so a
// direction-less key (used by every invalidateQueries call site) stays a prefix
// that matches all per-direction caches.
export function allocatedDownpaymentKey(blOrderId: string | undefined, direction?: InvoiceSide) {
  const base = ['bl-order-allocated-downpayment', blOrderId] as const;
  return direction === undefined ? base : ([...base, direction] as const);
}

export function useAllocatedDownpayment(blOrderId: string | undefined, direction?: InvoiceSide) {
  return useQuery<AllocatedDownpayment>({
    queryKey: allocatedDownpaymentKey(blOrderId, direction),
    enabled: blOrderId !== undefined && blOrderId.length > 0,
    queryFn: () => {
      const suffix = direction === undefined ? '' : `?direction=${direction}`;
      return apiRequest<AllocatedDownpayment>(
        `/bl-orders/${encodeURIComponent(blOrderId ?? '')}/allocated-downpayment${suffix}`,
      );
    },
  });
}
